import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {BulkActionProgress} from 'src/components/bulk-tool';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {
    bulkAssignUsersEntitlements,
    getAssignUsersEntitlementsProgress,
    stopBulkActions,
} from 'src/openapi/admin/apis/BulkAssignUsersEntitlementsApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import {
    selectAssignUsersEntitlementsBulkUuid,
    selectAssignUsersEntitlementsTask,
    setBackgroundTask,
    setShown,
    setUuid,
} from '../assign/assign-users-entitlements.slice';
import {
    TOOL_PROGRESS_LABEL,
    TOOL_STACK_OVERFLOW_MESSAGE,
    TOOL_TASK,
    TOOL_TITLE,
} from '../assign/constants';

import {
    clearData,
    updateUsersEntitlementsUsersSelector,
} from './slices/update-users-entitlements-users.slice';
import {entitlementsSelectionSelector} from './slices/update-users-entitlements.slice';
import {UpdateUsersEntitlementsView} from './update-users-entitlements-view';

import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';

export const AssignUsersEntitlements = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const createBackgroundTask = useBackgroundTaskManager();

    const {bulkOpen} = useEventTracking();
    const {bulkSubmit} = useEventTracking();

    const entitlementsSelection = useSelector(entitlementsSelectionSelector) as string[];
    const userIds = useSelector(updateUsersEntitlementsUsersSelector);

    const grantIds = useMemo(() => [...new Set(
        entitlementsSelection.map(id => JSON.parse(id.toString()).grantId).filter(Boolean),
    )], [entitlementsSelection]);

    const roleIds = useMemo(() => (
        entitlementsSelection.map(id => JSON.parse(id.toString()).roleId).filter(Boolean)
    ), [entitlementsSelection]);

    useEffect(() => {
        bulkOpen({toolName: TOOL_TITLE});

        return () => {
            clearData();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const cancelBulkAction = useCallback(() => {
        bulkSubmit({toolName: TOOL_TITLE, submit: false});
        navigate(-1);
    }, [navigate, bulkSubmit]);

    const processAction = useCallback(async (options?: ActionOptions) => {
        if (!userIds) {
            return 'No users selected';
        }

        if (!grantIds.length && !roleIds.length) {
            return 'No entitlements selected';
        }

        bulkSubmit({toolName: TOOL_TITLE, submit: true});

        const response = await apiDispatch(bulkAssignUsersEntitlements({
            ...options,
            updateEntitiesEntitlementsRelation: {
                entitiesIds: userIds,
                grantIds,
                roleIds,
            },
        }));

        if (response.payload.success) {
            navigate(-1);
            const uuid = response.payload.data;
            const backgroundTask = createBackgroundTask(
                TOOL_TASK,
                <BulkActionProgress
                    label={TOOL_PROGRESS_LABEL}
                    taskSelector={selectAssignUsersEntitlementsTask}
                    idSelector={selectAssignUsersEntitlementsBulkUuid}
                    getProgress={id => apiDispatch(getAssignUsersEntitlementsProgress({id}))}
                    stopAction={id => apiDispatch(stopBulkActions({id}))}
                    setShown={setShown}
                />,
                {maxStack: 1, stackOverflowMessage: TOOL_STACK_OVERFLOW_MESSAGE},
            );

            if (backgroundTask) {
                dispatch(setUuid(uuid));
                dispatch(setBackgroundTask(backgroundTask));
            }
        } else {
            return getError({error: response.payload.data, separator: ' '});
        }
    }, [
        bulkSubmit,
        navigate,
        grantIds,
        roleIds,
        userIds,
        createBackgroundTask,
        apiDispatch,
        dispatch,
    ]);

    const disabled = useMemo(() => (
        !entitlementsSelection.length || !userIds
    ), [entitlementsSelection, userIds]);

    return (
        <UpdateUsersEntitlementsView
            mode="ASSIGN"
            userIds={userIds}
            disabled={disabled}
            onCancelBulkAction={cancelBulkAction}
            onProcessAction={processAction}
        />
    );
};
